import type { IOrganization } from '@/domain/entities/organization';
import { OrgStatus } from '@/domain/shared/shared';

import { OrganizationFormFields } from '@/components/organizations/OrganizationFormFields';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ErrorNotice } from '@/components/ui/error-notice';
import type { OrganizationDetailsDraft, OrganizationSharedFieldErrors } from '@/lib/organization-form';

type OrganizationDetailsTabProps = {
  organization: IOrganization;
  draft: OrganizationDetailsDraft;
  createdByLabel: string;
  saving: boolean;
  hasChanges: boolean;
  canEditDetails: boolean;
  canEditCapabilities: boolean;
  canEditStatus: boolean;
  fieldErrors: OrganizationSharedFieldErrors;
  saveError: string | null;
  capabilitiesRawValue: string;
  onDraftChange: (patch: Partial<OrganizationDetailsDraft>) => void;
  onOpenCapabilitiesModal: () => void;
  onSave: () => void;
  onReset: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  formatDate: (timestamp?: number) => string;
};

export function OrganizationDetailsTab({
  organization,
  draft,
  createdByLabel,
  saving,
  hasChanges,
  canEditDetails,
  canEditCapabilities,
  canEditStatus,
  fieldErrors,
  saveError,
  capabilitiesRawValue,
  onDraftChange,
  onOpenCapabilitiesModal,
  onSave,
  onReset,
  onActivate,
  onDeactivate,
  formatDate,
}: OrganizationDetailsTabProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{organization.name}</CardTitle>
        <CardDescription className="space-y-1">
          <div>ID: {organization.id}</div>
          <div data-testid="organization-slug-value">Slug: {draft.slug}</div>
          <div data-testid="organization-created-by-value">Created by: {createdByLabel}</div>
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <OrganizationFormFields
            values={draft}
            errors={fieldErrors}
            disabled={!canEditDetails}
            onChange={(patch) => onDraftChange(patch)}
          />

          <div className="space-y-2">
            <Label>Status</Label>
            {canEditStatus ? (
              <Select value={draft.status} onValueChange={(value) => onDraftChange({ status: value as OrgStatus })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={OrgStatus.Active}>Active</SelectItem>
                  <SelectItem value={OrgStatus.Inactive}>Inactive</SelectItem>
                  <SelectItem value={OrgStatus.Suspended}>Suspended</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <div
                data-testid="organization-status-value"
                className="bg-muted/30 text-foreground rounded-md border px-3 py-2 text-sm"
              >
                {draft.status}
              </div>
            )}
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label>Capabilities</Label>
            <div className="space-y-3 rounded-md border p-4">
              <div className="flex items-center justify-between gap-2">
                <p className="text-muted-foreground text-xs">
                  Selected: {draft.capabilities.length} permission{draft.capabilities.length === 1 ? '' : 's'}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onOpenCapabilitiesModal}
                  disabled={!canEditCapabilities}
                >
                  Edit
                </Button>
              </div>
              {!canEditCapabilities ? (
                <p className="text-muted-foreground text-xs">
                  Organization capabilities can only be changed by a system admin.
                </p>
              ) : null}
              {!canEditDetails ? (
                <p className="text-muted-foreground text-xs">You have read-only access to organization details.</p>
              ) : null}

              <Textarea
                readOnly
                value={capabilitiesRawValue}
                rows={7}
                className="max-h-56 min-h-40 overflow-y-auto font-mono text-xs"
                placeholder="No capabilities assigned."
              />
            </div>
          </div>
        </div>

        <div className="grid gap-2 rounded-md border p-4 text-sm md:grid-cols-2">
          <div>
            <span className="text-muted-foreground">Created:</span> {formatDate(organization.createdAt)}
          </div>
          <div>
            <span className="text-muted-foreground">Updated:</span> {formatDate(organization.updatedAt)}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t pt-4">
          <div className="w-full">
            <ErrorNotice message={saveError} title="Failed to Save Organization" />
          </div>

          <Button type="button" onClick={onSave} disabled={!canEditDetails || !hasChanges || saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>

          <Button type="button" variant="outline" onClick={onReset} disabled={!canEditDetails || saving || !hasChanges}>
            Reset
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={onActivate}
            disabled={saving || draft.status === OrgStatus.Active || !canEditStatus}
          >
            Activate
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={onDeactivate}
            disabled={saving || draft.status === OrgStatus.Inactive || !canEditStatus}
          >
            Deactivate
          </Button>

          {hasChanges ? <span className="text-muted-foreground text-sm">You have unsaved changes</span> : null}
        </div>
      </CardContent>
    </Card>
  );
}
